import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEmployeeAuditLog } from '@/hooks/use-employee-audit-log';
import { usePermission } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';
import type { EmployeeDetail } from '@/types/employee';
import type { EmployeeAuditField } from '@/types/employee-audit';
import {
  historyAutoLoads,
  historyFieldFilters,
  historyFooterState,
  orderHistoryEntries,
} from '@/features/people/lib/employee-history';
import { HistoryRow } from './history-row';

/** The employee's change history [HW-795]. Shown only to holders of employees.audit.view. */
export function HistoryTab({
  employee,
  focusOnMount = false,
  onFocused,
}: {
  employee: EmployeeDetail;
  /** Move focus to the heading on mount: set when opened from the Job tab's "Show all history". */
  focusOnMount?: boolean;
  /** Called once the focus move has happened, so the parent can clear the flag. */
  onFocused?: () => void;
}) {
  const currentUser = useAuthStore((s) => s.user);
  const isSelf = currentUser?.employee?.id === employee.id;
  const canViewMessengers = usePermission('employees.emergency.view') || isSelf;
  const canViewSalaryScalars = usePermission('employees.salary.view');
  const canViewEducation = usePermission('employees.profile.view');
  const fieldFilters = historyFieldFilters({
    canViewMessengers,
    canViewSalaryScalars,
    canViewEducation,
  });
  const [field, setField] = useState<EmployeeAuditField | 'all'>('all');
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isRefetchError,
    isRefetching,
  } = useEmployeeAuditLog(employee.id, field);
  const footer = historyFooterState({
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isRefetchError,
    isRefetching,
  });
  const autoLoads = historyAutoLoads(footer);

  // A new observer fires its first callback at once when the sentinel is on
  // screen, so it must not exist while a fetch failed: otherwise every failure
  // re-arms it and the next page is retried in a loop. Retry re-enables it.
  // The feed scrolls inside its own box, so the sentinel is watched against
  // that box rather than the page. The box is keyed by the filter (below), so a
  // new filter mounts a new box and sentinel; `field` re-arms the observer on them.
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    if (focusOnMount) {
      headingRef.current?.focus();
      onFocused?.();
    }
    // Mount only: a later change of the flag must not steal focus.
  }, []);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !autoLoads) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) fetchNextPage();
      },
      { root: scrollRef.current, rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [autoLoads, fetchNextPage, field]);

  const entries = orderHistoryEntries(data?.pages.flatMap((p) => p.data) ?? []);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 ref={headingRef} tabIndex={-1} className="text-base font-medium outline-none">
          History
        </h2>
        <Select value={field} onValueChange={(v) => setField(v as EmployeeAuditField | 'all')}>
          <SelectTrigger className="h-9 w-[200px]" aria-label="Filter by field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fields</SelectItem>
            {fieldFilters.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div
          role="status"
          className="rounded-lg border border-border p-6 text-sm text-muted-foreground"
        >
          Loading...
        </div>
      ) : error && entries.length === 0 ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
        >
          Failed to load the history.
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-10 text-center">
          <p className="text-sm text-muted-foreground">
            {field === 'all'
              ? 'No changes recorded yet. Changes are recorded from the release of this feature on.'
              : 'No changes to this field recorded yet.'}
          </p>
        </div>
      ) : (
        // Keyed by the filter: a new filter's list starts at the top instead of
        // inheriting the old list's scroll position through a reused node.
        // `relative` makes the box the containing block of the rows' sr-only
        // (absolute) labels, so it clips them; otherwise the hidden rows' labels
        // stretch the page below the box.
        <div
          key={field}
          ref={scrollRef}
          className="relative max-h-[min(36rem,65vh)] overflow-y-auto rounded-lg border border-border bg-background"
        >
          <ul className="divide-y divide-border px-5">
            {entries.map((e) => (
              <HistoryRow key={e.id} entry={e} />
            ))}
          </ul>
          <div ref={sentinelRef} className="px-5 py-3 text-center">
            {footer === 'loading-more' ? (
              <span role="status" className="text-xs text-muted-foreground">
                Loading more...
              </span>
            ) : footer === 'refreshing' ? (
              <span role="status" className="text-xs text-muted-foreground">
                Refreshing...
              </span>
            ) : footer === 'next-page-error' ? (
              <div className="flex items-center justify-center gap-2 text-xs text-destructive">
                <span role="alert">Failed to load more.</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fetchNextPage()}
                  className="text-xs"
                >
                  Retry
                </Button>
              </div>
            ) : footer === 'refresh-error' ? (
              <div className="flex items-center justify-center gap-2 text-xs text-destructive">
                <span role="alert">Couldn't refresh the history.</span>
                <Button variant="ghost" size="sm" onClick={() => refetch()} className="text-xs">
                  Retry
                </Button>
              </div>
            ) : footer === 'load-more' ? (
              <Button variant="ghost" size="sm" onClick={() => fetchNextPage()} className="text-xs">
                Load more
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">End of history</span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
